// src/resources/Vehicles/VehiclePDFExport.js

import React from "react";
import jsPDF from "jspdf";
import { calculateRemainingFinance, vehicleStatusAsOf, getFinalInstallmentEnd, formatDeferredInstallments, getDeferredInstallmentCount, getDeferredInstallmentsTotal } from "./vehicleFinance";
import autoTable from "jspdf-autotable";

export default function VehiclePDFExport({ vehicles = [], asOfDate }) {
  const asOf = asOfDate ? new Date(asOfDate + "T00:00:00") : new Date();
  // ---------- Helpers ----------
  const formatDate = (d) => {
    if (!d) return "";
    const date = d.seconds ? new Date(d.seconds * 1000) : new Date(d);
    if (isNaN(date)) return "";
    return date.toLocaleDateString("en-GB");
  };

  const formatCurrency = (value) =>
    (Number(value) || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // ---------- Correct Remaining Finance (same logic as VehicleList) ----------
  const calculateRemaining = (v) => calculateRemainingFinance(v, asOf);

  // ---------- isCashCar (same rules as VehicleList) ----------
  const isCashCar = (v) => !["FINANCED", "DEFERRED"].includes(vehicleStatusAsOf(v, asOf));

  const vehicleNumberSortValue = (value) => {
    const text = String(value || "").trim();
    const match = text.match(/\d+/);
    return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
  };

  const sortByVehicleNumber = (a, b) => {
    const aNo = vehicleNumberSortValue(a?.vehicleNumber);
    const bNo = vehicleNumberSortValue(b?.vehicleNumber);

    if (aNo !== bNo) return aNo - bNo;

    return String(a?.vehicleNumber || "").localeCompare(
      String(b?.vehicleNumber || ""),
      undefined,
      { numeric: true, sensitivity: "base" }
    );
  };

  const getOrdinalSuffix = (n) => {
    if (n > 3 && n < 21) return "th";
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  // ---------- Main handler ----------
  const handleDownload = () => {
    if (!vehicles.length) {
      alert("No vehicles to export.");
      return;
    }

    // Enriched Data (raw values)
    const enriched = [...vehicles].sort(sortByVehicleNumber).map((v, idx) => {
      const remainingRaw = calculateRemaining(v);
      const cashFlag = isCashCar(v);
      const status = vehicleStatusAsOf(v, asOf);
      return {
        sn: idx + 1,
        company: v.company || "",
        vehicleNumber: v.vehicleNumber || "",
        vehicleType: v.vehicleType || "",
        vehicleYear: v.vehicleYear || "",
        insuranceExpiry: v.insuranceExpiry || null,
        registrationExpiry: v.registrationExpiry || null,
        mortgageFrom: cashFlag ? "" : v.mortgageFrom || "",
        installmentBank: cashFlag ? "" : v.installmentBank || "",
        monthlyInstallment: cashFlag ? "" : Number(v.monthlyInstallment) || 0,
        totalFacilityAmount: Number(v.totalFacilityAmount) || 0,
        installmentStart: cashFlag ? null : v.installmentStart || null,
        installmentEnd: cashFlag ? null : v.installmentEnd || null,
        finalInstallmentEnd: cashFlag ? null : getFinalInstallmentEnd(v),
        deferredInstallmentsText: cashFlag ? "" : formatDeferredInstallments(v),
        deferredCount: cashFlag ? "" : getDeferredInstallmentCount(v),
        deferredAmount: cashFlag ? "" : getDeferredInstallmentsTotal(v),
        bankCharges: cashFlag ? "" : Number(v.bankCharges) || 0,
        settlementDate: cashFlag ? null : v.settlementDate || null,
        settlementAmount: cashFlag ? "" : Number(v.settlementAmount) || 0,
        remainingFinance: cashFlag ? 0 : remainingRaw,
        status: cashFlag ? "Cash" : status,
        _isCash: cashFlag,
      };
    });

    const doc = new jsPDF("landscape");
    doc.setFont("helvetica", "normal");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const today = new Date().toLocaleDateString("en-GB");

    // ---------- Split cash / financed (for summary only) ----------
    const financedVehicles = enriched.filter((v) => !v._isCash);
    const cashVehicles = enriched.filter((v) => v._isCash);

    const totalMonthly = financedVehicles.reduce(
      (sum, v) => sum + (v.monthlyInstallment || 0),
      0
    );

    const totalRemaining = financedVehicles.reduce(
      (sum, v) => sum + (v.remainingFinance || 0),
      0
    );

    // ---------- Company Remaining Summary ----------
    const companyRemainingMap = {};
    financedVehicles.forEach((v) => {
      const c = v.company || "Unknown Company";
      companyRemainingMap[c] =
        (companyRemainingMap[c] || 0) + (v.remainingFinance || 0);
    });

    const grandRemaining = Object.values(companyRemainingMap).reduce(
      (a, b) => a + b,
      0
    );

    // ----- Remaining Finance Summary (Bank-wise Corrected) -----
const bankRemainingMap = {};
financedVehicles.forEach((v) => {
  const bank = v.mortgageFrom || "Unknown Bank";   // ✅ FIXED
  const remaining = v.remainingFinance || 0;

  bankRemainingMap[bank] = (bankRemainingMap[bank] || 0) + remaining;
});


    // ---------- Installment Summary (Bank → Day) ----------
    const bankGroups = {};
    financedVehicles.forEach((v) => {
      const bank = v.installmentBank || "Unknown Bank";
      let start = v.installmentStart;

      if (start?.seconds) start = new Date(start.seconds * 1000);
      else if (start) start = new Date(start);

      const day = start && !isNaN(start.getTime()) ? start.getDate() : 1;

      if (!bankGroups[bank]) bankGroups[bank] = {};
      if (!bankGroups[bank][day]) bankGroups[bank][day] = 0;

      bankGroups[bank][day] += v.monthlyInstallment || 0;
    });

    const sortedBanks = Object.keys(bankGroups).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );

    // ---------- PAGE 1: HEADER ----------
    doc.setFillColor(10, 45, 90);
    doc.rect(0, 0, pageWidth, 18, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.text("Vehicle Finance Report", 10, 11);
    doc.setFontSize(9);
    doc.text(`Generated: ${today}`, pageWidth - 60, 11);

    // ---------- Summary ----------
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);

    let y = 24;

    doc.text(`Total Vehicles: ${enriched.length}`, 10, y);
    doc.text(`Cash Vehicles: ${cashVehicles.length}`, 10, y + 5);
    doc.text(`Financed Vehicles: ${financedVehicles.length}`, 10, y + 10);

    doc.text(
      `Total Remaining Finance: AED ${formatCurrency(totalRemaining)}`,
      pageWidth / 2,
      y
    );
    doc.text(
      `Total Monthly Installment: AED ${formatCurrency(totalMonthly)}`,
      pageWidth / 2,
      y + 5
    );

    y += 20;

    // ---------- Remaining Finance Summary (Company-wise) ----------
    doc.setFillColor(10, 45, 90);
    doc.rect(10, y - 6, pageWidth - 20, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.text("Remaining Finance Summary (Company-wise)", 14, y - 1);

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    y += 8;

    Object.keys(companyRemainingMap)
      .sort()
      .forEach((name) => {
        doc.text(name, 14, y);
        doc.text(
          `AED ${formatCurrency(companyRemainingMap[name])}`,
          pageWidth - 14,
          y,
          { align: "right" }
        );
        y += 6;
      });

    doc.setFont("helvetica", "bold");
    doc.text("Grand Total:", 14, y + 2);
    doc.text(`AED ${formatCurrency(grandRemaining)}`, pageWidth - 14, y + 2, {
      align: "right",
    });
    doc.setFont("helvetica", "normal");

    y += 14;

    // ---------- Remaining Finance Summary (Bank-wise) ----------
    doc.setFillColor(10, 45, 90);
    doc.rect(10, y - 6, pageWidth - 20, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.text("Remaining Finance Summary (Bank-wise)", 14, y - 1);

    y += 8;
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    Object.keys(bankRemainingMap)
      .sort()
      .forEach((bank) => {
        doc.text(bank, 14, y);
        doc.text(
          `AED ${formatCurrency(bankRemainingMap[bank])}`,
          pageWidth - 14,
          y,
          { align: "right" }
        );
        y += 6;
      });

    y += 10;

    // ---------- Installment Summary (Improved, Clean, Professional) ----------
doc.setFillColor(10, 45, 90);
doc.rect(10, y - 6, pageWidth - 20, 8, "F");
doc.setTextColor(255, 255, 255);
doc.setFontSize(11);
doc.text("Vehicle Installment Summary (Excluding Cash)", 14, y - 1);

y += 10;
doc.setTextColor(0, 0, 0);
doc.setFontSize(10);

if (sortedBanks.length === 0) {
  doc.text("No financed vehicles found.", 14, y);
  y += 10;
} else {
  sortedBanks.forEach((bank) => {
    const dateMap = bankGroups[bank];
    const bankTotal = Object.values(dateMap).reduce((a, b) => a + b, 0);
    const days = Object.keys(dateMap)
      .map(Number)
      .sort((a, b) => a - b);

    // Bank Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(bank, 14, y);

    y += 7;

    // Table-style formatting
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    days.forEach((day) => {
      const lineLeft = `${day}${getOrdinalSuffix(day)} of each month`;
      const lineRight = `AED ${formatCurrency(dateMap[day])}`;

      doc.text(lineLeft, 20, y);
      doc.text(lineRight, pageWidth - 20, y, { align: "right" });

      y += 6;
    });

    // Bank Subtotal
    doc.setFont("helvetica", "bold");
    doc.text("Bank Total:", 20, y + 2);
    doc.text(`AED ${formatCurrency(bankTotal)}`, pageWidth - 20, y + 2, {
      align: "right",
    });
    doc.setFont("helvetica", "normal");

    y += 14;

    // Page break safety
    if (y > pageHeight - 40) {
      doc.addPage("landscape");
      y = 20;
    }
  });

  // Grand Total All Banks
  const grandMonthly = sortedBanks.reduce((sum, bank) => {
    return (
      sum +
      Object.values(bankGroups[bank]).reduce((a, b) => a + b, 0)
    );
  }, 0);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Grand Total (All Banks):", 14, y);
  doc.text(`AED ${formatCurrency(grandMonthly)}`, pageWidth - 20, y, {
    align: "right",
  });
  doc.setFont("helvetica", "normal");

  y += 10;
}


    // ---------- Vehicles Table (All cars together, cash = green) ----------
doc.addPage("landscape");
addSectionHeader(doc, "All Vehicles (Financed + Cash)");

// Reset cursor to avoid overlapping / unreadable characters
y = 26;


autoTable(doc, {
  startY: y,

  headStyles: {
    fillColor: [10, 45, 90],
    textColor: [255, 255, 255],
  },
  bodyStyles: { fontSize: 8 },
  theme: "grid",
  styles: { lineWidth: 0.1 },

  // ✅ Highlight cash cars correctly using the _isCash flag
didParseCell: (hook) => {
  const row = hook.row;
  if (!row || !row.raw) return;

  const v = row.raw; // enriched vehicle object

  if (v._isCash === true) {
    hook.cell.styles.fillColor = [210, 255, 210]; // light green
  }
},


  head: [
    [
      "S/N",
      "Company",
      "Vehicle No.",
      "Type / Model",
      "Year",
      "Mortgage From",
      "Installment Bank",
      "Monthly (AED)",
      "Start",
      "Original End",
      "Final End",
      "Deferred",
      "Charges",
      "Settlement",
      "Remaining (AED)",
      "Status",
    ],
  ],

  body: enriched.map((v) => [
    v.sn,
    v.company,
    v.vehicleNumber,
    v.vehicleType,
    v.vehicleYear,
    v.mortgageFrom,
    v.installmentBank,
    v._isCash ? "" : formatCurrency(v.monthlyInstallment),
    formatDate(v.installmentStart),
    formatDate(v.installmentEnd),
    formatDate(v.finalInstallmentEnd),
    v._isCash ? "" : v.deferredInstallmentsText || "-",
    v._isCash ? "" : formatCurrency(v.bankCharges),
    formatDate(v.settlementDate),
    `AED ${formatCurrency(v.remainingFinance)}`, // always formatted correctly
    v.status,
  ]),
});

doc.save("Vehicles_Report.pdf");

  };

  const addSectionHeader = (doc, title) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFillColor(10, 45, 90);
    doc.rect(0, 0, pageWidth, 16, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(title, 10, 11);
    doc.setFont("helvetica", "normal");
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      style={{
        background: "#0b4a7a",
        color: "white",
        border: "none",
        padding: "6px 12px",
        borderRadius: 6,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 500,
        marginRight: 8,
      }}
    >
      Download PDF
    </button>
  );
}
